import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Spinner,
  IconButton,
  Tooltip,
  Tabs,
  TabsHeader,
  Tab,
  ButtonGroup,
  Button,
} from '@material-tailwind/react';
import {
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';

const DEVICE_SIZES = {
  mobile: { width: 375, label: 'Mobile' },
  tablet: { width: 768, label: 'Tablet' },
  desktop: { width: '100%', label: 'Desktop' },
};

function PreviewPane({
  htmlContent,
  shareableContent,
  isLoading,
  isLoadingShareable,
  isApplyingExclusions,
  error,
  errorShareable,
  onRefresh,
  onRefreshShareable,
  selectedSection,
}) {
  const [activeTab, setActiveTab] = useState('print');
  const [deviceSize, setDeviceSize] = useState('desktop');

  // Use a key to force iframe re-render when content changes
  const [iframeKey, setIframeKey] = useState(0);
  const [shareableIframeKey, setShareableIframeKey] = useState(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);
  const shareableIframeRef = useRef(null);

  useEffect(() => {
    if (htmlContent) {
      setIframeKey((prev) => prev + 1);
      setIframeLoaded(false);
    }
  }, [htmlContent]);

  useEffect(() => {
    if (shareableContent) {
      setShareableIframeKey((prev) => prev + 1);
    }
  }, [shareableContent]);

  // Listen for postMessage from iframe (for Plotly chart interactions)
  useEffect(() => {
    const handleMessage = (event) => {
      // Handle excludePoints message from iframe (user clicked Exclude button)
      if (event.data?.type === 'excludePoints') {
        const excludedIds = event.data.excludedIds || [];
        // Re-dispatch as custom event on parent window
        window.dispatchEvent(
          new CustomEvent('excludePoints', {
            detail: { excludedIds },
          })
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Listen for clearSelection event and forward to iframe
  useEffect(() => {
    const handleClearSelection = () => {
      if (shareableIframeRef.current?.contentWindow) {
        shareableIframeRef.current.contentWindow.postMessage(
          { type: 'clearSelection' },
          '*'
        );
      }
    };

    window.addEventListener('clearSelection', handleClearSelection);
    return () => window.removeEventListener('clearSelection', handleClearSelection);
  }, []);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);


  // Scroll to section when selectedSection changes (print preview only)
  useEffect(() => {
    if (!selectedSection || !iframeLoaded || !iframeRef.current || activeTab !== 'print') return;

    try {
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (!iframeDoc) return;

      // Try different ID formats the backend might use
      const possibleIds = [
        `section-${selectedSection}`,
        selectedSection,
        `${selectedSection}-section`,
      ];

      for (const id of possibleIds) {
        const element = iframeDoc.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }

      // Fallback: try to find by data attribute
      const dataElement = iframeDoc.querySelector(`[data-section="${selectedSection}"]`);
      if (dataElement) {
        dataElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      // Silently fail if scroll doesn't work
    }
  }, [selectedSection, iframeLoaded, activeTab]);

  const currentLoading = activeTab === 'print' ? isLoading : isLoadingShareable;
  const currentError = activeTab === 'print' ? error : errorShareable;
  const handleRefresh = activeTab === 'print' ? onRefresh : onRefreshShareable;

  const getIframeWidth = () => {
    if (activeTab === 'print' || deviceSize === 'desktop') return '100%';
    return DEVICE_SIZES[deviceSize].width;
  };

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-hidden">
        {/* Header with tabs and controls */}
        <div className="flex items-center justify-between mb-4">
          <Tabs value={activeTab} className="w-auto">
            <TabsHeader className="bg-gray-100 p-1">
              <Tab value="print" onClick={() => setActiveTab('print')} className="px-4 py-1.5">
                <Typography variant="small" className="font-medium">
                  Print Preview
                </Typography>
              </Tab>
              <Tab value="client" onClick={() => setActiveTab('client')} className="px-4 py-1.5">
                <Typography variant="small" className="font-medium">
                  Client View
                </Typography>
              </Tab>
            </TabsHeader>
          </Tabs>

          <div className="flex items-center gap-2">
            {/* Device size toggle - only for client view */}
            {activeTab === 'client' && (
              <ButtonGroup size="sm" variant="outlined" className="mr-2">
                <Tooltip content="Mobile">
                  <Button
                    className={`px-2 ${deviceSize === 'mobile' ? 'bg-gray-100' : ''}`}
                    onClick={() => setDeviceSize('mobile')}
                  >
                    <DevicePhoneMobileIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
                <Tooltip content="Tablet">
                  <Button
                    className={`px-2 ${deviceSize === 'tablet' ? 'bg-gray-100' : ''}`}
                    onClick={() => setDeviceSize('tablet')}
                  >
                    <DeviceTabletIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
                <Tooltip content="Desktop">
                  <Button
                    className={`px-2 ${deviceSize === 'desktop' ? 'bg-gray-100' : ''}`}
                    onClick={() => setDeviceSize('desktop')}
                  >
                    <ComputerDesktopIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </ButtonGroup>
            )}

            {handleRefresh && (
              <Tooltip content="Refresh preview">
                <IconButton variant="text" size="sm" onClick={handleRefresh}>
                  <ArrowPathIcon className="h-4 w-4" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Preview content */}
        <div className="flex-1 border rounded-lg overflow-hidden relative bg-gray-100 flex items-center justify-center">
          {currentLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <Spinner className="h-8 w-8" />
            </div>
          )}
          {currentError && (
            <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
              <Typography color="red" variant="small">
                Failed to load preview: {currentError}
              </Typography>
            </div>
          )}

          {/* Print Preview iframe */}
          {activeTab === 'print' && (
            <iframe
              ref={iframeRef}
              key={iframeKey}
              title="Print Preview"
              className="w-full h-full border-0 bg-white"
              srcDoc={htmlContent}
              sandbox="allow-same-origin allow-scripts"
              onLoad={handleIframeLoad}
            />
          )}

          {/* Client View iframe */}
          {activeTab === 'client' && (
            <div
              className="h-full bg-white transition-all duration-300 overflow-hidden relative"
              style={{
                width: getIframeWidth(),
                maxWidth: '100%',
                boxShadow: deviceSize !== 'desktop' ? '0 0 20px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              {/* Exclusion loading overlay */}
              {isApplyingExclusions && (
                <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-20">
                  <svg className="animate-spin h-8 w-8 text-amber-500 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <Typography variant="small" className="font-medium text-amber-700">
                    Updating analysis...
                  </Typography>
                  <Typography variant="small" color="gray" className="text-xs mt-1">
                    Recalculating metrics with updated data
                  </Typography>
                </div>
              )}
              <iframe
                ref={shareableIframeRef}
                key={shareableIframeKey}
                title="Client View"
                className="w-full h-full border-0"
                srcDoc={shareableContent}
                sandbox="allow-same-origin allow-scripts"

              />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default PreviewPane;
